import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutDashboard, Users, CalendarDays, Wallet, FileText,
  BarChart3, Shield, Settings, LogOut, Menu, X, Boxes
} from 'lucide-react';
import { logoutThunk, selectUser, selectPlan } from '../../store/authSlice';

const NAV = [
  { to: '/dashboard',   label: 'Dashboard',        icon: LayoutDashboard },
  { to: '/employees',   label: 'Employees',         icon: Users           },
  { to: '/attendance',  label: 'Attendance & Leave', icon: CalendarDays    },
  { to: '/payroll',     label: 'Payroll',           icon: Wallet          },
  { to: '/payslips',    label: 'Payslips',          icon: FileText        },
  { to: '/reports',     label: 'Reports',           icon: BarChart3       },
  { to: '/compliance',  label: 'Compliance',        icon: Shield          },
  { to: '/settings',    label: 'Settings',          icon: Settings        },
];

const PLAN_BADGE = {
  starter: 'bg-amber-500/15 text-amber-300 ring-amber-400/20',
  pro:     'bg-emerald-500/15 text-emerald-300 ring-emerald-400/20',
};

const SidebarContent = ({ onItemClick }) => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const user      = useSelector(selectUser);
  const plan      = useSelector(selectPlan);
  const initials  = user?.ownerName?.split(' ').map((n) => n[0]).join('').slice(0, 2) || 'SS';
  const badgeCls  = PLAN_BADGE[plan] || PLAN_BADGE.starter;

  const handleLogout = async () => {
    onItemClick?.();
    await dispatch(logoutThunk());
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-full bg-[#1E3A5F] text-white">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-white/10">
        <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center ring-1 ring-white/20">
          <Boxes className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-base font-semibold tracking-tight font-heading">SlipSense</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Payroll OS</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto sidebar-scroll">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 px-3 mb-2">Workspace</div>
        <ul className="space-y-0.5">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                      isActive
                        ? 'bg-[#2E6DA4] text-white shadow-sm'
                        : 'text-slate-300 hover:bg-white/5 hover:text-white'
                    }`
                  }
                >
                  <Icon className="w-[18px] h-[18px]" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User footer */}
      <div className="border-t border-white/10 px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#2E6DA4] flex items-center justify-center text-sm font-semibold ring-1 ring-white/20">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.ownerName || 'Owner'}</div>
            <div className="text-xs text-slate-400 truncate">{user?.businessName || 'Business'}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ring-1 ${badgeCls}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {plan === 'pro' ? 'Pro' : 'Starter'}
          </span>
          <button
            onClick={handleLogout}
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <aside className="hidden md:flex w-64 flex-shrink-0 h-screen sticky top-0 z-30">
        <SidebarContent />
      </aside>

      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 w-10 h-10 rounded-md bg-[#1E3A5F] text-white flex items-center justify-center shadow-lg"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-md bg-white/10 text-white flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
            <SidebarContent onItemClick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};